import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail, ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14 lg:px-10" data-testid="privacy-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
      </Link>

      <div className="mt-6 flex items-center gap-3">
        <div className="grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-primary">Politique</p>
          <h1 className="font-[Outfit] text-3xl font-normal tracking-tight sm:text-4xl">
            Confidentialité &amp; collecte des données
          </h1>
        </div>
      </div>

      <p className="mt-6 text-sm text-muted-foreground">
        Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}
      </p>

      <article className="prose prose-neutral mt-8 max-w-none space-y-6 text-sm leading-relaxed text-foreground/90">
        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">1. Qui sommes-nous ?</h2>
          <p>
            Saor (« nous », « notre service ») est une plateforme de mise en relation entre hôtels et chauffeurs privés VTC.
            Nous proposons aux établissements hôteliers de réserver rapidement un chauffeur pour leurs clients,
            et aux chauffeurs validés d'accepter ces courses.
          </p>
          <p>
            Responsable du traitement : <strong>Saor</strong>, situé à Nice, France.
            Pour toute question relative à cette politique, contactez-nous à{" "}
            <a href="mailto:saor.pro@icloud.com" className="text-primary underline-offset-2 hover:underline">
              saor.pro@icloud.com
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">2. Données que nous collectons</h2>
          <h3 className="mt-3 font-[Outfit] text-base font-medium text-foreground">Comptes Hôtels</h3>
          <ul className="ml-5 list-disc space-y-1">
            <li>Nom de l'établissement, adresse, nom et coordonnées du responsable (email, téléphone).</li>
            <li>Fréquence prévue de réservations et préférences opérationnelles.</li>
          </ul>

          <h3 className="mt-3 font-[Outfit] text-base font-medium text-foreground">Comptes Chauffeurs</h3>
          <ul className="ml-5 list-disc space-y-1">
            <li>Identité (nom, prénom, adresse), pièce d'identité, photo de profil.</li>
            <li>Documents légaux : carte VTC, permis de conduire, KBIS / SIRENE, RIB, attestations d'assurance, certificat d'inscription au registre VTC, macaron.</li>
            <li>Informations véhicule : marque, modèle, plaque d'immatriculation, photos intérieures et extérieures.</li>
            <li>Position GPS en temps réel pendant les services (uniquement lorsque le chauffeur est en ligne) afin de proposer les courses adéquates et calculer un ETA précis.</li>
          </ul>

          <h3 className="mt-3 font-[Outfit] text-base font-medium text-foreground">Données de course (clients finaux)</h3>
          <ul className="ml-5 list-disc space-y-1">
            <li>Prénom du client, numéro de téléphone (utilisé pour le suivi et la communication chauffeur).</li>
            <li>Adresses de départ et d'arrivée, nombre de passagers et de bagages.</li>
            <li>Statut de paiement et historique des courses.</li>
          </ul>

          <h3 className="mt-3 font-[Outfit] text-base font-medium text-foreground">Données techniques</h3>
          <ul className="ml-5 list-disc space-y-1">
            <li>Adresse IP, agent utilisateur (navigateur), identifiants de session.</li>
            <li>Tokens de notifications push (OneSignal, Web Push VAPID) pour vous alerter sur vos appareils.</li>
            <li>Logs d'accès aux APIs pour la sécurité et la lutte anti-fraude.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">3. Pourquoi collectons-nous ces données ?</h2>
          <ul className="ml-5 list-disc space-y-1">
            <li>Mettre en relation hôtels, chauffeurs et clients pour exécuter une course.</li>
            <li>Vérifier la conformité légale des chauffeurs avant de leur autoriser l'accès à la plateforme.</li>
            <li>Gérer les paiements via notre prestataire <strong>Square</strong> (Tap-to-Pay sur téléphone du chauffeur).</li>
            <li>Calculer les itinéraires et ETA via <strong>Mapbox</strong>.</li>
            <li>Envoyer les notifications de course (OneSignal, Web Push, e-mail via Resend).</li>
            <li>Améliorer la qualité du service et prévenir la fraude.</li>
            <li>Respecter nos obligations légales, comptables et fiscales (factures, TVA).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">4. Partage de vos données</h2>
          <p>
            Nous ne vendons jamais vos données. Nous les partageons uniquement avec les sous-traitants strictement nécessaires :
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li><strong>Square</strong> — encaissement Tap-to-Pay sur le téléphone du chauffeur.</li>
            <li><strong>Mapbox</strong> — géocodage, itinéraires et cartes.</li>
            <li><strong>OneSignal</strong> — notifications push mobiles et web.</li>
            <li><strong>Resend</strong> — envoi d'e-mails transactionnels.</li>
            <li><strong>Firebase Realtime Database (Google)</strong> — messagerie temps réel et diffusion de la position du chauffeur pendant une course active.</li>
          </ul>
          <p>
            Chacun de ces sous-traitants est tenu par contrat à une utilisation limitée à ses missions et conforme au RGPD.
          </p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">5. Durée de conservation</h2>
          <ul className="ml-5 list-disc space-y-1">
            <li>Données de compte : pendant toute la durée d'utilisation, supprimées sur demande.</li>
            <li>Documents légaux des chauffeurs : 5 ans après la fin de la relation, pour répondre aux obligations légales.</li>
            <li>Historique de courses et factures : 10 ans (obligation comptable française).</li>
            <li>Logs techniques : 12 mois maximum.</li>
            <li>Position GPS temps réel : effacée dès que la course est terminée (Firebase RTDB).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">6. Vos droits (RGPD)</h2>
          <p>
            Conformément au Règlement Général sur la Protection des Données (UE 2016/679), vous disposez des droits suivants :
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li>Droit d'accès, de rectification, et de portabilité de vos données.</li>
            <li>Droit à l'effacement (« droit à l'oubli »).</li>
            <li>Droit d'opposition au traitement et de limitation du traitement.</li>
            <li>Droit de retirer votre consentement à tout moment.</li>
            <li>Droit d'introduire une réclamation auprès de la CNIL (cnil.fr).</li>
          </ul>
        </section>

        <section className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-5">
          <h2 className="flex items-center gap-2 font-[Outfit] text-xl font-medium text-foreground">
            <Mail className="h-5 w-5 text-primary" />
            7. Suppression de votre compte
          </h2>
          <p className="mt-2">
            Pour toute demande de suppression de votre compte et de vos données associées,
            veuillez nous contacter à l'adresse suivante :
          </p>
          <p className="mt-2">
            <a
              href="mailto:saor.pro@icloud.com?subject=Demande%20de%20suppression%20de%20compte%20Saor"
              className="text-base font-medium text-primary underline-offset-2 hover:underline"
              data-testid="privacy-deletion-email"
            >
              saor.pro@icloud.com
            </a>
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Précisez l'adresse e-mail liée à votre compte. Votre demande sera traitée sous 30 jours maximum,
            conformément au RGPD. Les documents légaux soumis à conservation obligatoire (factures, KBIS, etc.)
            pourront néanmoins être conservés pour la durée légale prévue.
          </p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">8. Cookies &amp; stockage local</h2>
          <p>
            Saor utilise un cookie d'authentification et un stockage local (localStorage / sessionStorage) pour :
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li>Maintenir votre session connectée.</li>
            <li>Mémoriser le brouillon d'une réservation en cours et vos adresses récentes.</li>
            <li>Prévenir les tentatives de connexion frauduleuses.</li>
          </ul>
          <p>Aucun cookie publicitaire ou de pistage tiers n'est posé.</p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">9. Sécurité</h2>
          <p>
            Toutes les communications transitent en HTTPS. Les mots de passe sont chiffrés avec bcrypt.
            Les paiements sont délégués à Square (PCI-DSS Level 1). Nos serveurs sont hébergés au sein
            de l'Union européenne.
          </p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">10. Modifications</h2>
          <p>
            Cette politique peut être mise à jour pour refléter les évolutions de la plateforme ou de la
            réglementation. Toute modification substantielle vous sera notifiée par e-mail et la date de
            mise à jour ci-dessus sera modifiée en conséquence.
          </p>
        </section>
      </article>
    </div>
  );
}
